<script>
import cloneDeep from 'lodash/cloneDeep'
import {
  agentDetailsComputed,
  agentDetailsMethods,
} from '@state/helpers/agent-details'
import { agentListMethods } from '@state/helpers/agent-list'
import { STATUS_REQUEST, ROLE } from '@utils/constant'
import basicInfoItems from './basicInfoItems'

export default {
  props: {
    triggerMask: {
      type: Boolean,
      require: false,
    },
  },
  data() {
    return {
      items: cloneDeep(basicInfoItems),
      editable: false,
      openConfirm: false,
      ROLE: ROLE,
    }
  },
  computed: {
    ...agentDetailsComputed,
    itemsComputed() {
      if (this.getDetails) {
        const details = this.getDetails
        return this.items?.map((item) => {
          item.value = details[item.key]
          if (item.key === 'management_url') {
            item.value = `${process.env.VUE_APP_API_BASE_URL}/alliance/${details.agent_code}`
          }
          return item
        })
      }
      return this.items
    },
    currentUserLevel() {
      const user = this.$store.getters['auth/getCurrentUser']
      return user?.level
    },
    errorServerSideComputed() {
      return this.getEditErrors
    },
  },
  watch: {
    getEditStatus() {
      // if edit success toggle editable to readonly
      if (this.getEditStatus === STATUS_REQUEST.SUCCESS) {
        this.editable = false
      }
    },
  },
  methods: {
    ...agentListMethods,
    ...agentDetailsMethods,
    toggleEditable() {
      this.items = this.itemsComputed
      this.editable = !this.editable
      this.$emit('enable:edit', this.editable)
    },
    async handleDeleteAgent() {
      const { id } = this.$route.params
      const response = await this.deleteAgentById({ id })
      if (response) {
        this.$router.push({ name: 'agentList' })
      }
    },
    updateBasicInfo() {
      const payload = this.items.reduce((results, item) => {
        results[item.key] = item.value
        return results
      }, {})
      this.updateAgent({ ...this.getDetails, ...payload })
    },
  },
}
</script>

<template>
  <div id="agent-details-basic-info">
    <BaseCardDetail
      :errors="errorServerSideComputed"
      title="基本情報"
      form-collapsed
      :trigger-mask="!editable && triggerMask"
      :has-footer="editable"
      @edit="toggleEditable"
      @cancel="toggleEditable"
      @submit="updateBasicInfo"
    >
      <template v-slot:header-action="{ edit }">
        <CButton
          v-show="
            currentUserLevel === ROLE.SUPER ||
              currentUserLevel === ROLE.AGENT_ADMIN ||
              currentUserLevel === ROLE.AGENT_SUB
          "
          variant="ghost"
          @click="edit"
        >
          <CIcon name="cil-pencil" />
        </CButton>
        <CButton
          v-if="currentUserLevel === ROLE.SUPER"
          variant="ghost"
          @click="openConfirm = !openConfirm"
        >
          <CIcon name="cil-trash" />
        </CButton>
      </template>
      <CRow
        v-for="item in itemsComputed"
        :key="item.key"
        class="d-flex align-items-baseline"
      >
        <CCol sm="4">
          {{ $t(`fields.${item.label}`) }}
          <span v-if="item.rules && item.rules.required" class="text-danger"
            >*</span
          >
        </CCol>
        <CCol
          v-if="currentUserLevel === ROLE.SUPER"
          sm="8"
          :class="{
            'd-flex align-items-baseline flex-wrap': item.key === 'agent_code',
          }"
        >
          <BaseInput
            v-if="
              item.as === 'input' &&
                item.key !== 'phone' &&
                item.key !== 'email'
            "
            v-model="item.value"
            :rules="editable && item.rules"
            :name="item.name"
            :vee-name="item.name"
            :type="item.type"
            :plaintext="!editable"
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
          />
          <BaseInput
            v-if="item.as === 'input' && item.key === 'email' && editable"
            v-model="item.value"
            :rules="editable && item.rules"
            :name="item.name"
            :vee-name="item.name"
            :type="item.type"
            :plaintext="!editable"
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
          />
          <BaseInput
            v-if="item.as === 'input' && item.key === 'email' && !editable"
            v-model="item.value"
            v-c-tooltip="{
              content: item.value,
              placement: 'top',
            }"
            :name="item.name"
            :vee-name="item.name"
            :type="item.type"
            :plaintext="!editable"
            class="limit-one-line"
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
            color="primary"
          />
          <BaseInput
            v-if="item.as === 'input' && item.key === 'phone'"
            v-model="item.value"
            :rules="editable && item.rules"
            :name="item.name"
            :vee-name="item.name"
            :type="item.type"
            :plaintext="!editable"
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
          />
          <BaseInput
            v-if="item.as === 'plaintext'"
            :value="item.value"
            :type="item.type"
            plaintext
          />
          <BaseQrcodeDownload
            v-if="item.key === 'agent_code'"
            :text="item.value"
          />
        </CCol>
        <CCol
          v-if="
            currentUserLevel === ROLE.AGENT_SUB ||
              currentUserLevel === ROLE.AGENT_ADMIN
          "
          sm="8"
          :class="{
            'd-flex align-items-baseline flex-wrap': item.key === 'agent_code',
          }"
        >
          <BaseInput
            v-if="item.as === 'input' && item.key === 'email' && editable"
            v-model="item.value"
            :rules="editable && item.rules"
            :name="item.name"
            :vee-name="item.name"
            :type="item.type"
            :plaintext="!editable"
            :class="{
              'mr-3 custom-grow': item.key === 'agent_code',
            }"
          />
          <BaseInput
            v-if="item.as === 'input' && item.key === 'email' && !editable"
            v-model="item.value"
            v-c-tooltip="{
              content: item.value,
              placement: 'top',
            }"
            :rules="
              editable && {
                ...item.rules,
                ...{ required: item.value.length <= 0 },
              }
            "
            :name="item.name"
            :vee-name="item.name"
            :type="item.type"
            :plaintext="!editable"
            class="limit-one-line"
            :class="{
              'mr-3 custom-grow': item.key === 'agent_code',
            }"
          />
          <BaseInput
            v-if="item.as === 'input' && item.key === 'phone'"
            v-model="item.value"
            :rules="editable && item.rules"
            :name="item.name"
            :vee-name="item.name"
            :type="item.type"
            :plaintext="!editable"
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
          />
          <BaseInput
            v-if="
              (item.as === 'input' && item.key === 'company') ||
                item.key === 'admin_name' ||
                item.key === 'agent_code'
            "
            v-model="item.value"
            :name="item.name"
            :type="item.type"
            plaintext
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
          />
          <BaseInput
            v-if="item.as === 'plaintext'"
            :value="item.value"
            :type="item.type"
            plaintext
          />
          <BaseQrcodeDownload
            v-if="item.key === 'agent_code'"
            :text="item.value"
          />
        </CCol>
        <CCol
          v-if="
            currentUserLevel === ROLE.AGENT_AUDIT ||
              currentUserLevel === ROLE.AGENT_ANNOUNCE
          "
          sm="8"
          :class="{
            'd-flex align-items-baseline flex-wrap': item.key === 'agent_code',
          }"
        >
          <BaseInput
            v-if="item.as === 'input' && item.name !== 'email'"
            v-model="item.value"
            :name="item.name"
            :type="item.type"
            plaintext
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
          />
          <BaseInput
            v-if="item.as === 'input' && item.name === 'email'"
            v-model="item.value"
            v-c-tooltip="{
              content: item.value,
              placement: 'top',
            }"
            :name="item.name"
            :type="item.type"
            plaintext
            class="limit-one-line"
            :class="{ 'mr-3 custom-grow': item.key === 'agent_code' }"
          />
          <BaseInput
            v-if="item.as === 'plaintext'"
            :value="item.value"
            :type="item.type"
            plaintext
          />
          <BaseQrcodeDownload
            v-if="item.key === 'agent_code'"
            :text="item.value"
          />
        </CCol>
      </CRow>
      <BaseModalConfirm
        :show.sync="openConfirm"
        type="delete"
        backdrop-close
        @closeModal="openConfirm = !openConfirm"
        @submit="handleDeleteAgent"
      />
    </BaseCardDetail>
  </div>
</template>

<style lang="scss" scoped>
#agent-details-basic-info {
  ::v-deep .custom-grow {
    flex-grow: 0.5;
  }
}
::v-deep .limit-one-line input {
  display: -webkit-box;
  max-height: 2rem;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
}
</style>
